import type { ChangeEvent, SyntheticEvent } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { BaseInput } from '../BaseInput';
import type { BaseInputProps } from '../BaseInput';
import { ColorPickerBox } from './ColorPickerBox';

export interface ColorPickerProps
  extends Omit<
    BaseInputProps,
    | 'autoComplete'
    | 'actionContent'
    | 'customContent'
    | 'showInvalidIcon'
    | 'defaultValue'
    | 'onChange'
    | 'type'
    | 'value'
    | 'isInvalid'
    | 'required'
    | 'icon'
    | 'ref'
  > {
  /**
   * The hex color value.
   */
  value: string;
  /**
   * Callback when the color value is changed.
   */
  onChange?: (color: string, isValid: boolean) => void;
}

const VALID_HEX_REGEXP = /^#([A-F0-9]{3}|[A-F0-9]{6})$/i;

const validateHexColor = (color: string) => VALID_HEX_REGEXP.test(color);

const Wrapper = styled.div`
  position: relative;
`;

const ColorInput = styled(BaseInput)<{ value: string }>`
  &:before {
    content: '';
    display: block;
    width: 1em;
    height: 1em;
    flex-shrink: 0;
    margin-left: ${tokens.spacer.x2};
    background: ${({ value }) => value};
    border: 1px solid ${tokens.color.ui_border};
  }
`;

const ColorPickerWrapper = styled.div`
  z-index: 2;
  display: none;
  position: absolute;
  background-color: ${tokens.color.ui_background};
  padding: ${tokens.spacer.x2};
  ${tokens.elevation(2)};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.m};
  margin-top: ${tokens.spacer.x1};

  ${Wrapper}:focus-within & {
    display: block;
  }
`;

/**
 * ColorPicker input allows users to select colors.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/009271-color-picker Usage guidelines}
 *
 * {@link http://storybook.hotjar.com/?path=/story/components-colorpicker--color-picker Code documentation}
 *
 * @example
 * <ColorPicker value="#ff0" />
 */
export const ColorPicker = forwardRef<HTMLInputElement, ColorPickerProps>(
  (
    {
      className,
      value,
      isDisabled = false,
      isRequired = false,
      isReadonly = false,
      size = 'm',
      onChange,
      ...props
    }: ColorPickerProps,
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(value);

    const changeColor = (color: string) => {
      setInternalValue(color);
      onChange && onChange(color, validateHexColor(color));
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      changeColor(e.target.value);
    };

    const onWidgetMouseDown = (e: SyntheticEvent<HTMLSpanElement>) => {
      // making sure the input won't loose focus when the picker box is clicked
      e.nativeEvent.preventDefault();
    };

    useEffect(() => {
      if (value !== internalValue) {
        setInternalValue(value);
      }
    }, [value]);

    return (
      <Wrapper className={className}>
        <ColorInput
          {...props}
          icon={null}
          ref={ref}
          value={internalValue}
          autoComplete="off"
          isInvalid={!validateHexColor(internalValue)}
          isDisabled={isDisabled}
          isReadonly={isReadonly}
          isRequired={isRequired}
          size={size}
          onChange={handleInputChange}
        />
        {!isReadonly && !isDisabled && (
          <ColorPickerWrapper
            data-testid="color-picker-box"
            onMouseDown={onWidgetMouseDown}
          >
            <ColorPickerBox color={internalValue} onChange={changeColor} />
          </ColorPickerWrapper>
        )}
      </Wrapper>
    );
  },
);

ColorPicker.displayName = 'ColorPicker';
